// Hvordan en by ser ud på kortet, som en ren funktion af state (ingen Pixi her). Renderen, HUD'en og
// sandhedsmålingen (src/ai/audit.ts) bruger den samme funktion, så målingen ser det, spilleren ser.
//
// Spor "sandhed" (anmelderne: "hjerter tabes ved 82-89 %, mens byen ser tændt ud"): en by er kun
// tændt, når den får nok OG dens mørkeur står på nul. Så snart uret løber, dæmpes byen gradvist, og
// fra halvt ur blinker den med et hjerte over taget: det hjerte er det, der ryger, når uret er fuldt.

import type { GridNode, State } from '../sim/types';
import { clockFull } from '../sim/cities';

/** Fra så stor en del af uret blinker byen (6 s ur: mindst 3 s blink før hjertet ryger). */
export const BLINK_FROM = 0.5;

export interface CityLook {
  /** Byen ser glad og tændt ud. */
  lit: boolean;
  /** Hvor dæmpet byen er, 0 (tændt) til 1 (sort). */
  dim: number;
  /** Uret er over halvt: byen blinker, og et hjerte står over den. */
  alarm: boolean;
  /** Mørkeurets andel, 0-1. */
  clock: number;
}

/** Får byen nok strøm lige nu (som simmens isPowered, men falsk uden forbrug og når den er slukket)? */
export function isLit(n: GridNode): boolean {
  const c = n.city;
  if (!c) return false;
  return !c.out && c.demandMw > 0 && c.servedMw >= c.demandMw * 0.9;
}

/** Byens udseende på kortet. */
export function cityLook(state: State, n: GridNode): CityLook {
  const c = n.city;
  if (!c) return { lit: false, dim: 0, alarm: false, clock: 0 };
  const full = Math.max(1, clockFull(state));
  const frac = Math.min(1, c.clock / full);
  const fed = isLit(n);
  // Slukket: sort. Står den stadig uden strøm efter mørketiden, løber uret igen mod et nyt hjerte,
  // og så blinker den også i mørket.
  if (c.out) return { lit: false, dim: 1, alarm: c.darkTicks <= 0 && c.servedMw < c.demandMw * 0.9 && frac >= BLINK_FROM, clock: c.darkTicks > 0 ? 1 : frac };
  if (fed && c.clock <= 0) return { lit: true, dim: 0, alarm: false, clock: 0 };
  if (c.demandMw <= 0) return { lit: false, dim: 0.4, alarm: false, clock: frac };
  // Uret løber (eller tømmes igen): dæmpet efter, hvor langt det er nået.
  const dim = fed ? 0.25 : 0.35 + 0.55 * frac;
  return { lit: false, dim, alarm: !fed && frac >= BLINK_FROM, clock: frac };
}
